/* eslint-disable @typescript-eslint/no-explicit-any */
import * as Yup from "yup";
import { BaseSchema } from "yup";
import { AnyObject, Maybe } from "yup/lib/types";

Yup.setLocale({
  mixed: {
    default: "入力エラーです",
    required: ({ label }) => `${label}は必須入力です`,
    oneOf: ({ label, values }) =>
      `${label}は次の値のいずれかでなければなりません: ${values as string}`,
    notOneOf: ({ label, values }) =>
      `${label}は次の値のいずれかであってはなりません: ${values as string}`,
    notType: ({ label }) => `${label}の形式が違います`,
  },
  string: {
    length: ({ label, length }) =>
      `${label}は${length.toString()}文字入力して下さい`,
    min: ({ label, min }) =>
      `${label}は${min.toString()}文字以上入力してください`,
    max: ({ label, max }) =>
      `${label}は${max.toString()}文字以内で入力して下さい`,
    matches: ({ label }) => `${label}の形式が違います`,
    email: ({ label }) => `${label}の形式が違います`,
    url: ({ label }) => `${label}の形式が違います`,
    trim: ({ label }) => `${label}は前後にスペースを入れてはいけません`,
    lowercase: ({ label }) => `${label}は小文字でなければなりません`,
    uppercase: ({ label }) => `${label}は大文字でなければなりません`,
  },
  number: {
    min: ({ label, min }) =>
      `${label}は${min.toString()}以上の値を入力して下さい`,
    max: ({ label, max }) =>
      `${label}は${max.toString()}以下の値を入力して下さい`,
    lessThan: ({ label, less }) =>
      `${label}は${less.toString()}より小さい値を入力して下さい`,
    moreThan: ({ label, more }) =>
      `${label}は${more.toString()}より大きい値を入力して下さい`,
    positive: ({ label }) => `${label}は正の数を入力して下さい`,
    negative: ({ label }) => `${label}は負の数を入力して下さい`,
    integer: ({ label }) => `${label}は整数を入力して下さい`,
  },
  date: {
    min: ({ label, min }) =>
      `${label}は${min.toString()}以上の日付を入力して下さい`,
    max: ({ label, max }) =>
      `${label}は${max.toString()}以下の日付を入力して下さい`,
  },
  object: {
    noUnknown: ({ label }) =>
      `${label}は有効なキーを持ったデータを入力して下さい`,
  },
  array: {
    min: ({ label, min }) =>
      `${label}は${min.toString()}個以上の値を入力して下さい`,
    max: ({ label, max }) =>
      `${label}は${max.toString()}個以下の値を入力して下さい`,
  },
});

Yup.addMethod(Yup.string, "katakana", function () {
  return this.test(
    "katakana",
    ({ label }) => `${label}は全角カタカナで入力して下さい`,
    (value) => {
      if (value === undefined || value == null || value === "") {
        return true;
      }
      // eslint-disable-next-line no-irregular-whitespace
      return /^[ァ-ヶー　 ]+$/.test(value);
    },
  );
});

Yup.addMethod(Yup.string, "lineId", function () {
  return this.test(
    "lineId",
    ({ label }) =>
      `${label}は半角英数字（a〜z,0〜9）と.（ドット）、-（ハイフン）、_（アンダーバー）で入力して下さい`,
    (value) => {
      if (value === undefined || value == null || value === "") {
        return true;
      }
      return /^[a-z0-9-_.]+$/.test(value);
    },
  );
});

Yup.addMethod(Yup.string, "facebookId", function () {
  return this.test(
    "facebookId",
    ({ label }) =>
      `${label}は半角英数字（a〜z,0〜9）と.（ドット）、-（ハイフン）、_（アンダーバー）で入力して下さい`,
    (value) => {
      if (value === undefined || value == null || value === "") {
        return true;
      }
      return /^[a-z0-9.]+$/.test(value);
    },
  );
});

Yup.addMethod(Yup.string, "twitterId", function () {
  return this.test(
    "twitterId",
    ({ label }) =>
      `${label}は半角英数字（a〜z,A〜Z,0〜9）と_（アンダーバー）で入力して下さい`,
    (value) => {
      if (value === undefined || value == null || value === "") {
        return true;
      }
      return /^[a-zA-Z0-9_]+$/.test(value);
    },
  );
});

Yup.addMethod(Yup.string, "instagramId", function () {
  return this.test(
    "instagramId",
    ({ label }) =>
      `${label}は半角英数字（a〜z,0〜9）と.（ドット）、_（アンダーバー）で入力して下さい`,
    (value) => {
      if (value === undefined || value == null || value === "") {
        return true;
      }
      return /^[a-z0-9._]+$/.test(value);
    },
  );
});

Yup.addMethod(Yup.string, "tel", function () {
  return this.test("tel", `ハイフンがあってもなくても入力できる形`, (value) => {
    if (value === undefined || value == null || value === "") {
      return true;
    }
    return /^0[-0-9]{9,15}$/.test(value);
  });
});

Yup.addMethod(Yup.string, "date", function () {
  return this.test(
    "date",
    ({ label }) => `${label}は日時（yyyy/mm/dd）の形式で入力して下さい`,
    (value) => {
      if (value === undefined || value == null || value === "") {
        return true;
      }
      return /^\d{4}\/\d{2}\/\d{2}$/.test(value);
    },
  );
});

declare module "yup" {
  interface StringSchema<
    TType extends Maybe<string> = string | undefined,
    TContext extends AnyObject = AnyObject,
    TOut extends TType = TType,
  > extends BaseSchema<TType, TContext, TOut> {
    katakana(): StringSchema<TType, TContext>;
    tel(): StringSchema<TType, TContext>;
    date(): StringSchema<TType, TContext>;
    lineId(): StringSchema<TType, TContext>;
    facebookId(): StringSchema<TType, TContext>;
    twitterId(): StringSchema<TType, TContext>;
    instagramId(): StringSchema<TType, TContext>;
  }
}

export default Yup;
